/**
 * @file Hook wrapping the `getConfigurationSession` callable. Consumer:
 * `useSignifyConfigurationSidebar`, which resumes a punch-out session the operator left open.
 * `execute` rethrows the callable's own error, so this hook adds no message of its own and the
 * caller sees the `HttpsError` intact; `error` stays populated for the sidebar's banner.
 */

import { useCallback } from 'react';
import { SignifyConfigurationSessionResponse, SIGNIFY_CONFIG } from '#types/SignifyApi.types';
import { getConfigurationSession } from '../services/signifyApiService';
import { useSignifyApi } from './useSignifyApi';

export const useSignifyConfigurationSession = () => {
  const api = useSignifyApi<SignifyConfigurationSessionResponse>();

  const fetchConfigurationSession = useCallback(
    async (
      customerId: string,
      distributorId: string,
      sessionId: string,
      fields: 'BASIC' | 'DEFAULT' | 'FULL' = SIGNIFY_CONFIG.defaultFields,
    ): Promise<SignifyConfigurationSessionResponse> =>
      api.execute(() => getConfigurationSession(customerId, distributorId, sessionId, fields)),
    [api],
  );

  return {
    loading: api.loading,
    error: api.error,
    configurationSessionData: api.data,
    fetchConfigurationSession,
    clearConfigurationSession: api.clear,
  };
};
